import { useAvatarSrc, useLogout, usePermissions, useSession } from '@boothiva/auth';
import { AppShell, Breadcrumbs, Sidebar, ThemeToggle, Topbar, UserMenu } from '@boothiva/ui';
import { Building2, CalendarDays, CreditCard, FolderOpen, Gauge, History, LayoutTemplate, LifeBuoy, Mail, Monitor, Settings, Users } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Outlet, useLocation, useMatches, useNavigate } from 'react-router';
import { endSupportSession } from '../../features/support/api';
import lockup from '../../assets/boothiva-lockup.png';
import lockupReverse from '../../assets/boothiva-lockup-reverse.webp';
type RouteHandle = { crumb?: string };

/** Where "Get support" writes to. The same address the marketing site publishes; change it in one place. */
const supportEmail = 'help@boothiva.com';

/* Both lockups ship; CSS shows the one that suits the current theme. The alt text sits on the light one so
   a screen reader announces the brand exactly once. */
const logo = <>
  <img className="bv-logo bv-logo--light" src={lockup} alt="Boothiva" width={280} height={86} />
  <img className="bv-logo bv-logo--reverse" src={lockupReverse} alt="" aria-hidden="true" width={280} height={86} />
</>;

function ImpersonationBanner() {
  const { session } = useSession();
  const [error, setError] = useState('');
  const end = useMutation({
    mutationFn: endSupportSession,
    onSuccess: () => { window.location.assign('/login'); },
    onError: () => setError('Could not end the support session. Try again.'),
  });
  if (!session?.impersonation?.active) return null;
  const expires = new Date(session.impersonation.expiresAtUtc).toLocaleTimeString();
  return <div className="bv-impersonation-banner" role="alert">
    <span>Support access active — signed in as this account by <strong>{session.impersonation.byEmail}</strong>. Expires {expires}.</span>
    {error && <span>{error}</span>}
    <button type="button" disabled={end.isPending} onClick={() => end.mutate()}>{end.isPending ? 'Ending…' : 'End session'}</button>
  </div>;
}

export function AppLayout() {
  const location = useLocation(); const matches = useMatches(); const { has } = usePermissions();
  const logout = useLogout(); const navigate = useNavigate(); const { session } = useSession();
  const user = session?.user; const avatarSrc = useAvatarSrc(user?.avatarUrl); const displayName = user ? `${user.firstName} ${user.lastName}`.trim() || user.email : '';
  const crumbs = matches.map((match) => ({ handle: match.handle as RouteHandle, pathname: match.pathname })).filter((match) => match.handle.crumb).map((match) => ({ label: match.handle.crumb!, href: match.pathname }));
  const items = [
    { href: '/', label: 'Dashboard', icon: <Gauge aria-hidden="true" size={20} />, current: location.pathname === '/' },
    { href: '/events', label: 'Events', icon: <CalendarDays aria-hidden="true" size={20} />, current: location.pathname.startsWith('/events') },
    ...(has('templates.template.view') ? [{ href: '/templates', label: 'Templates', icon: <LayoutTemplate aria-hidden="true" size={20} />, current: location.pathname.startsWith('/templates') }] : []),
    { href: '/assets', label: 'Assets', icon: <FolderOpen aria-hidden="true" size={20} />, current: location.pathname.startsWith('/assets') },
    { href: '/devices', label: 'Devices', icon: <Monitor aria-hidden="true" size={20} />, current: location.pathname.startsWith('/devices') },
    { href: '/team', label: 'Team', icon: <Users aria-hidden="true" size={20} />, current: location.pathname.startsWith('/team') },
    {
      type: 'group' as const, label: 'Settings', icon: <Settings aria-hidden="true" size={20} />,
      children: [
        { href: '/settings/business', label: 'Business', icon: <Building2 aria-hidden="true" size={16} />, current: location.pathname.startsWith('/settings/business') },
        ...(has('settings.tenant.email') ? [{ href: '/settings/email', label: 'Email', icon: <Mail aria-hidden="true" size={16} />, current: location.pathname.startsWith('/settings/email') }] : []),
        ...(has('billing.subscription.view') ? [{ href: '/settings/billing', label: 'Billing', icon: <CreditCard aria-hidden="true" size={16} />, current: location.pathname.startsWith('/settings/billing') }] : []),
      ],
    },
    ...(has('audit.entry.view') ? [{ href: '/audit', label: 'Audit history', icon: <History aria-hidden="true" size={20} />, current: location.pathname.startsWith('/audit') }] : []),
  ];
  const helpCard = <div className="bv-sidebar__card">
    <span className="bv-sidebar__card-icon" aria-hidden="true"><LifeBuoy size={18} /></span>
    <h2>Need help?</h2>
    <p>We're here to help you create great events.</p>
    <a className="bv-button" href={`mailto:${supportEmail}`}>Get support</a>
  </div>;
  const appFooter = <>
    <span className="bv-app-footer__brand">{logo}</span>
    <span>© {new Date().getFullYear()} Boothiva. Making memories, everywhere.</span>
    <span>Developed by <a href="https://diyonix.com.au" target="_blank" rel="noreferrer">Diyonix</a></span>
    <nav aria-label="Legal">
      <a href="https://boothiva.com/legal/privacy">Privacy</a>
      <a href="https://boothiva.com/legal/terms">Terms</a>
      <a href={`mailto:${supportEmail}`}>Support</a>
    </nav>
  </>;
  return <><ImpersonationBanner /><AppShell footer={appFooter} sidebar={<Sidebar brand={logo} items={items} footer={helpCard} />} topbar={<Topbar breadcrumbs={<Breadcrumbs items={crumbs} />} actions={<><ThemeToggle />{user && <UserMenu name={displayName} email={user.email} avatarUrl={avatarSrc} onProfile={() => { void navigate('/profile'); }} onLogout={() => { void logout(); }} />}</>} />}><Outlet /></AppShell></>;
}
